import * as React from 'react';

import { datadogRum } from '@datadog/browser-rum';
import Footer from '@lendi-jam/footer';
import { type Breadcrumb } from '@lendi-jam/generic-hero-image';
import Legal from '@lendi-ui/legal-stuff';
import { EventCategory } from '@lendi/analytics-web';
import { useSession } from '@lendi/lala-react';
import { graphql } from 'gatsby';
import type { PageProps } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import PrismicCategoryHeroImage from '#components/categoryPage/PrismicHeroImage';
import PrismicFeaturePanel from '#components/currentLandingPage/PrismicFeaturePanel';
import Navbar from '#components/Navbar';
import PrismicCategoryCard from '#components/PrismicCategoryCard';
import PrismicGenericComplaintForm from '#components/PrismicGenericComplaintForm';
import PrismicContentHero from '#components/PrismicGenericContentHero';
import PrismicGenericDebtConsolidationCalculator from '#components/PrismicGenericDebtConsolidationCalculator';
import PrismicGenericEquityCalculator from '#components/PrismicGenericEquityCalculator';
import PrismicGenericFeaturedContent from '#components/PrismicGenericFeaturedContent';
import PrismicGenericImage from '#components/PrismicGenericImage';
import PrismicGenericInlineCta from '#components/PrismicGenericInlineCta';
import PrismicGenericLmiCalculator from '#components/PrismicGenericLmiCalculator';
import PrismicGenericOffsetCalculator from '#components/PrismicGenericOffsetCalculator';
import PrismicGenericRepaymentsCalculator from '#components/PrismicGenericRepaymentsCalculator';
import PrismicGenericRichText from '#components/PrismicGenericRichText';
import PrismicGenericTestimonial from '#components/PrismicGenericTestimonial';
import PrismicGenericTextImageLayout from '#components/PrismicGenericTextImageLayout';
import PrismicGenericVideo from '#components/PrismicGenericVideo';
import PrismicHeroImage from '#components/PrismicHeroImage';
import PrismicLenders from '#components/PrismicLenders';
import PrismicLoanOptionsWidget from '#components/PrismicLoanOptionsWidget';
import PrismicPropertySearchHero from '#components/PrismicPropertySearchHero';
import PrismicTable from '#components/PrismicTable';
import PrismicTextImageLayout from '#components/PrismicTextImageLayout';
import PrismicTextLayout from '#components/PrismicTextLayout';
import SEOHead, { PageMetadataProps, SchemaProps } from '#components/SEOHead';
import SlicesContent from '#contentTypes';
import { CATEGORY_CARD } from '#contentTypes/CategoryCard';
import { CATEGORY_HERO_IMAGE } from '#contentTypes/categoryPage/HeroImage';
import { CONTENT_HERO_IMAGE } from '#contentTypes/ContentHeroImage';
import { FEATURE_PANEL_GENERIC } from '#contentTypes/currentLandingPage/FeaturePanel';
import { GENERIC_COMPLAINT_FORM } from '#contentTypes/GenericComplaintForm';
import { GENERIC_DEBT_CONSOLIDATION_CALCULATOR } from '#contentTypes/GenericDebtConsolidationCalculator';
import { GENERIC_EQUITY_CALCULATOR } from '#contentTypes/GenericEquityCalculator';
import { GENERIC_FEATURED_CONTENT } from '#contentTypes/GenericFeaturedContent';
import { GENERIC_HERO_IMAGE } from '#contentTypes/GenericHeroImage';
import { GENERIC_IMAGE } from '#contentTypes/GenericImage';
import { GENERIC_INLINE_CTA } from '#contentTypes/GenericInlineCta';
import { GENERIC_LMI_CALCULATOR } from '#contentTypes/GenericLmiCalculator';
import { GENERIC_OFFSET_CALCULATOR } from '#contentTypes/GenericOffsetCalculator';
import { GENERIC_PROPERTY_SEARCH_HERO } from '#contentTypes/GenericPropertySearchHero';
import { GENERIC_REPAYMENTS_CALCULATOR } from '#contentTypes/GenericRepaymentsCalculator';
import { GENERIC_RICH_TEXT } from '#contentTypes/GenericRichText';
import { GENERIC_TESTIMONIAL } from '#contentTypes/GenericTestimonial';
import { GENERIC_TEXT_IMAGE_LAYOUT } from '#contentTypes/GenericTextImageLayout';
import { GENERIC_VIDEO } from '#contentTypes/GenericVideo';
import { GENERIC_LENDERS } from '#contentTypes/Lenders';
import { LOAN_OPTIONS_WIDGET } from '#contentTypes/LoanOptionsWidget';
import { TABLE } from '#contentTypes/Table';
import { TEXT_IMAGE_LAYOUT } from '#contentTypes/TextImageLayout';
import { TEXT_LAYOUT_ONE, TEXT_LAYOUT_TWO } from '#contentTypes/TextLayout';

import { PRISMIC_REPOSITORY_NAME } from '../../prismic/config';
import { linkResolver } from '../../prismic/utils/linkResolver';
import { useApplication, usePageTracking } from '../hooks';
import { CONTINUE_URL } from '../lib/url';

export interface GenericTemplateProps {
  data: GenericTemplateInnerProps;
}
export interface GenericTemplateInnerProps extends PageMetadataProps {
  body: SlicesContent[];
}

type GenericTemplateData = {
  prismicGenericTemplate: GenericTemplateProps;
  prismicSchema: SchemaProps;
};

type GenericTemplatePageContext = {
  rootQuery: string;
  uid: string;
  breadcrumbs: Breadcrumb[];
};

export function Head({ data }: PageProps<GenericTemplateData>) {
  const { body, ...meta } = data.prismicGenericTemplate.data;
  const schema = data.prismicSchema;
  return (
    <SEOHead meta={meta} schema={schema} />
  )
}


const Page: React.FC<PageProps<GenericTemplateData, GenericTemplatePageContext>> = ({
  data,
  pageContext: { breadcrumbs, uid },
}) => {
  const session = useSession();
  const application = useApplication(session);
  usePageTracking(EventCategory.JAM_LANDING_PAGES, `jam-${uid}`);
  const hasUtm = typeof window !== 'undefined' ? window.location.search.includes('utm_campaign') : undefined;
  const globalWindow: Window | undefined = typeof window !== 'undefined' ? window : undefined;

  if (!(data && data.prismicGenericTemplate)) {
    if (globalWindow) {
      window.location.assign('/404');
    }
    return null;
  }

  const { body } = data.prismicGenericTemplate.data;

  const continueUrl = !!(application && application.id) ? `v2/dashboard/application/${application.id}` : CONTINUE_URL;
  // const shouldOpenModal = !!(application && application.createdAt);
  const shouldOpenModal = false;

  return (
    <>
      <Navbar
        useTransparent
        showContinueAction
        continueURL={`/${continueUrl}`}
        homeURL={'/'}
        hasUtm={hasUtm}
        onLogout={session.logout}
        onException={(error) => {
          datadogRum.addError(error);
        }}
        enableChatBubble={true}
      />
      {body.map((component, i) => {
        switch (component.__typename) {
          case CONTENT_HERO_IMAGE:
            return <PrismicContentHero component={component} breadcrumbs={breadcrumbs} key={i} />;
          case GENERIC_HERO_IMAGE:
            return (
              <PrismicHeroImage
                shouldOpenModal={shouldOpenModal}
                component={component}
                breadcrumbs={breadcrumbs}
                key={i}
              />
            );
          case GENERIC_RICH_TEXT:
            return <PrismicGenericRichText component={component} key={i} />;
          case GENERIC_INLINE_CTA:
            return <PrismicGenericInlineCta component={component} key={i} />;
          case GENERIC_IMAGE:
            return <PrismicGenericImage component={component} key={i} />;
          case GENERIC_REPAYMENTS_CALCULATOR:
            return <PrismicGenericRepaymentsCalculator component={component} key={i} />;
          case GENERIC_OFFSET_CALCULATOR:
            return <PrismicGenericOffsetCalculator component={component} key={i} />;
          case GENERIC_LMI_CALCULATOR:
            return <PrismicGenericLmiCalculator component={component} key={i} />;
          case GENERIC_DEBT_CONSOLIDATION_CALCULATOR:
            return <PrismicGenericDebtConsolidationCalculator component={component} key={i} />;
          case GENERIC_EQUITY_CALCULATOR:
            return <PrismicGenericEquityCalculator component={component} key={i} />;
          case GENERIC_VIDEO:
            return <PrismicGenericVideo component={component} key={i} />;
          case GENERIC_FEATURED_CONTENT:
            return <PrismicGenericFeaturedContent component={component} key={i} />;
          case CATEGORY_HERO_IMAGE:
            return <PrismicCategoryHeroImage component={component} key={i} />;
          case GENERIC_PROPERTY_SEARCH_HERO:
            return <PrismicPropertySearchHero component={component} key={i} />;
          case GENERIC_TESTIMONIAL:
            return <PrismicGenericTestimonial component={component} key={i} />;
          case GENERIC_TEXT_IMAGE_LAYOUT:
            return <PrismicGenericTextImageLayout component={component} key={i} />;
          case LOAN_OPTIONS_WIDGET:
            return <PrismicLoanOptionsWidget component={component} key={i} />;
          case CATEGORY_CARD:
            return <PrismicCategoryCard component={component} key={i} />;
          case TEXT_LAYOUT_ONE:
            return <PrismicTextLayout component={component} layoutVariant="one" key={i} />;
          case TEXT_LAYOUT_TWO:
            return <PrismicTextLayout component={component} layoutVariant="two" key={i} />;
          case FEATURE_PANEL_GENERIC:
            return <PrismicFeaturePanel component={component} key={i} boldTitle />;
          case TEXT_IMAGE_LAYOUT:
            return <PrismicTextImageLayout component={component} key={i} />;
          case TABLE:
            return <PrismicTable component={component} key={i} />;
          case GENERIC_LENDERS:
            return <PrismicLenders component={component} key={i} />;
          case GENERIC_COMPLAINT_FORM:
            return <PrismicGenericComplaintForm key={i} />
          default:
            datadogRum.addError(new Error('GenericTemplate: unknown component.__typename: ' + component.__typename));
            return;
        }
      })}
      <Footer />
      <Legal />
    </>
  );
};

export default withPrismicPreview(Page, [
  {
    repositoryName: PRISMIC_REPOSITORY_NAME,
    linkResolver,
  },
]);

export const query = graphql`
  fragment GenericTemplateSchema on PrismicGenericTemplate {
    _previewable
    data {
      title {
        text
      }
      description {
        text
      }
      open_graph_type {
        text
      }
      open_graph_url {
        text
      }
      open_graph_image {
        url
      }
      twitter_card {
        text
      }
      twitter_creator {
        text
      }
      twitter_site {
        text
      }
      canonical_url {
        link_type
        url
      }
      body {
        ... on PrismicGenericTemplateDataBodyHeroImage {
          __typename
          items {
            additional_component_type
            component_icon
            component_text {
              text
            }
            button_link {
              link_type
              url
            }
            visible_on_mobile
          }
          primary {
            background_color
            alignment
            component_type
            partner_logo {
              alt
              fluid(maxWidth: 1440) {
                aspectRatio
                base64
                sizes
                src
                srcSet
              }
            }
            partner_logo_height
            lendi_logo_variant
            hero_component_image {
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            hero_component_image_mobile {
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            hero_image_disclaimer {
              text
            }
            hero_image_subtitle {
              html
            }
            hero_image_title {
              text
            }
            primary_button {
              text
            }
            primary_button_link {
              url
              link_type
            }
            secondary_button {
              text
            }
            secondary_button_link {
              url
              link_type
            }
            hide_breadcrumbs
          }
        }
        ... on PrismicGenericTemplateDataBodyContentPageHero {
          __typename
          primary {
            hero_image_subtitle {
              html
            }
            hero_image_title {
              text
            }
            hero_image_variant
            hide_breadcrumbs
            is_full_width
          }
        }
        ... on PrismicGenericTemplateDataBodyRichText {
          __typename
          primary {
            is_full_width
            rich_text_content {
              html
            }
            text_alignment
          }
        }
        ... on PrismicGenericTemplateDataBodyInlineCta {
          __typename
          primary {
            cta_heading {
              text
            }
            cta_body {
              text
            }
            cta_primary_button {
              text
            }
            cta_primary_button_link {
              url
              link_type
            }
            cta_secondary_button {
              text
            }
            cta_secondary_button_link {
              url
              link_type
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyImageComponent {
          __typename
          primary {
            image {
              url
            }
            link_url {
              link_type
              url
            }
            size
          }
        }
        ... on PrismicGenericTemplateDataBodyVideoComponent {
          __typename
          primary {
            video {
              html
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyFeaturedContent {
          __typename
          primary {
            featured_title {
              text
            }
          }
          items {
            content_description {
              text
            }
            content_image {
              url
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            content_link {
              link_type
              url
            }
            content_title {
              text
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyCategoryPageHero {
          __typename
          primary {
            hero_component_image {
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            hero_image_variant
            hero_image_subtitle {
              html
            }
            hero_image_title {
              text
            }
            primary_button {
              text
            }
            primary_button_link {
              url
              link_type
            }
            secondary_button {
              text
            }
            secondary_button_link {
              url
              link_type
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyPropertySearchHero {
          __typename
          primary {
            main_title {
              text
            }
            subtitle {
              text
            }
            background_image_sharp {
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            background_image_mobile {
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyCardCollection {
          __typename
          primary {
            card_collection_title {
              text
            }
            card_collection_subtitle {
              html
            }
            card_collection_variant
          }
          items {
            card_description {
              text
            }
            card_title {
              text
            }
            primary_button_link {
              url
              link_type
            }
            primary_button_text {
              text
            }
            secondary_button_link {
              link_type
              url
            }
            secondary_button_text {
              text
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyTwoTextLayout {
          __typename
          primary {
            variant
            text_layout_title {
              text
            }
            left_section_title {
              text
            }
            left_section_content {
              html
            }
            left_section_primary_button_text {
              text
            }
            left_section_primary_button_link {
              url
              link_type
            }
            left_section_secondary_button_text {
              text
            }
            left_section_secondary_button_link {
              url
              link_type
            }
            right_section_title {
              text
            }
            right_section_content {
              html
            }
            right_section_primary_button_text {
              text
            }
            right_section_primary_button_link {
              url
              link_type
            }
            right_section_secondary_button_text {
              text
            }
            right_section_secondary_button_link {
              url
              link_type
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyLmiCalc {
          __typename
          primary {
            calculator_heading {
              text
            }
            result_heading {
              text
            }
            result_body {
              text
            }
            button_link {
              url
            }
            button_text {
              text
            }
            disclaimer {
              text
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyDebtConsolidationCalculator {
          __typename
          primary {
            result_heading {
              text
            }
            result_body {
              text
            }
            button_link {
              url
            }
            button_text {
              text
            }
            disclaimer {
              text
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyRepaymentsCalc {
          __typename
          primary {
            calculator_heading {
              text
            }
            radio_tooltip_content {
              html
            }
            result_heading {
              text
            }
            result_body {
              text
            }
            button_link {
              url
            }
            button_text {
              text
            }
            graph_heading {
              text
            }
            disclaimer {
              text
            }
            show_graph
          }
        }
        ... on PrismicGenericTemplateDataBodyOffsetCalc {
          __typename
          primary {
            calculator_heading {
              text
            }
            contributions_tooltip_content {
              html
            }
            offset_delay_tooltip_content {
              html
            }
            result_heading {
              text
            }
            result_body {
              text
            }
            button_link {
              url
            }
            button_text {
              text
            }
            graph_heading {
              text
            }
            disclaimer {
              text
            }
            show_graph
          }
        }
        ... on PrismicGenericTemplateDataBodyEquityCalc {
          __typename
          primary {
            calculator_heading {
              text
            }
            radio_tooltip_content {
              html
            }
            help_text {
              html
            }
            result_heading {
              text
            }
            result_body {
              text
            }
            button_link {
              url
            }
            button_text {
              text
            }
            disclaimer {
              text
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyOneTextLayout {
          __typename
          primary {
            variant
            text_layout_title {
              text
            }
            text_layout_content {
              html
            }
            text_layout_primary_button_text {
              text
            }
            text_layout_primary_button_link {
              url
              link_type
            }
            text_layout_secondary_button_text {
              text
            }
            text_layout_secondary_button_link {
              url
              link_type
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyFeaturePanel {
          __typename
          primary {
            small_icons
            bold_panel_titles
            variant
            heading {
              text
            }
            subheading {
              text
            }
            button {
              text
            }
            button_link {
              link_type
              url
            }
            small_icons
          }
          items {
            panel_image {
              url
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
              alt
            }
            panel_icon
            panel_subtitle {
              text
            }
            panel_title {
              text
            }
            panel_link {
              link_type
              url
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyTextImageLayout {
          __typename
          primary {
            layout_title {
              text
            }
            content {
              html
            }
            primary_button_text {
              text
            }
            primary_button_link {
              url
              link_type
            }
            secondary_button_text {
              text
            }
            secondary_button_link {
              url
              link_type
            }
            image {
              alt
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            variant
            layout_variant
          }
        }
        ... on PrismicGenericTemplateDataBodyTextImageLayout1 {
          __typename
          primary {
            heading {
              text
            }
            primary_button_link {
              url
              link_type
            }
            primary_button_text {
              text
            }
            secondary_button_text {
              text
            }
            secondary_button_link {
              url
              link_type
            }
            image {
              alt
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            variant
            hide_image_mobile
          }
          items {
            step_title {
              text
            }
            step_description {
              html
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyLoanOptionsWidget {
          __typename
          primary {
            hero_heading {
              text
            }
            hero_subtitle {
              text
            }
            appointment_text {
              text
            }
            appointment_subtitle {
              text
            }
            appointment_button_text {
              text
            }
            appointment_button_link {
              url
              link_type
            }
            image_car {
              url
              alt
              fluid(maxWidth: 600) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            image_personal {
              url
              alt
              fluid(maxWidth: 600) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            image_business {
              url
              alt
              fluid(maxWidth: 600) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            image_equipment {
              url
              alt
              fluid(maxWidth: 600) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            image_home {
              url
              alt
              fluid(maxWidth: 600) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyTestimonial {
          __typename
          primary {
            heading {
              text
            }
            subtitle {
              text
            }
            button_link {
              url
              link_type
            }
            logo {
              alt
              fluid(maxWidth: 1440) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
            variant
          }
          items {
            review_title {
              text
            }
            text {
              text
            }
            author {
              text
            }
            location {
              text
            }
            date
          }
        }
        ... on PrismicGenericTemplateDataBodyTable {
          __typename
          primary {
            column_1_alignment
            column_2_alignment
            column_3_alignment
            column_4_alignment
            column_5_alignment
            column_6_alignment
            table_heading {
              text
            }
            table_subheading {
              html
            }
          }
          items {
            row_variant
            column_1_value {
              html
            }
            column_2_value {
              html
            }
            column_3_value {
              html
            }
            column_4_value {
              html
            }
            column_5_value {
              html
            }
            column_6_value {
              html
            }
          }
        }
        ... on PrismicGenericTemplateDataBodyComplaintForm {
          __typename
        }
        ... on PrismicGenericTemplateDataBodyLenders {
          __typename
          items {
            logo
          }
          primary {
            toggle_new_version
            heading {
              text
            }
            subheading {
              text
            }
            more_link {
              text
            }
            more_link_url {
              link_type
              url
            }
            primary_button_text {
              text
            }
            primary_button_link {
              link_type
              url
            }
            secondary_button_text {
              text
            }
            secondary_button_link {
              link_type
              url
            }
          }
        }
      }
      parent {
        document {
          ... on PrismicGenericTemplate {
            uid
            data {
              parent {
                document {
                  ... on PrismicGenericTemplate {
                    uid
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  query GenericPageQuery($uid: String!) {
    prismicGenericTemplate(uid: { eq: $uid }) {
      ...GenericTemplateSchema
    }
    prismicSchema {
      ...SchemaInformation
    }
  }
`;
